import type { ModalOverlayProps as RACModalOverlayProps } from "react-aria-components";

import {
  composeRenderProps,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

type SheetSide = "left" | "right";

type ClassNames = {
  content?: RACModalOverlayProps["className"];
  overlay?: RACModalOverlayProps["className"];
};

type SheetProps = {
  classNames?: ClassNames;
  side?: SheetSide;
} & Omit<RACModalOverlayProps, "className">;

const slideAnimations: Record<SheetSide, { enter: string; exit: string }> = {
  left: {
    enter: "animate-in slide-in-from-left",
    exit: "animate-out slide-out-to-left",
  },
  right: {
    enter: "animate-in slide-in-from-right",
    exit: "animate-out slide-out-to-right",
  },
};

export function Sheet({ classNames, side = "left", ...props }: SheetProps) {
  const animations = slideAnimations[side];

  return (
    <RACModalOverlay
      isDismissable
      {...props}
      className={composeRenderProps(
        classNames?.overlay,
        (className, renderProps) =>
          twMerge(
            "fixed inset-0 z-50",
            "bg-bg-overlay/50",
            "flex",
            side === "left" && "justify-start",
            side === "right" && "justify-end",
            renderProps.isEntering &&
              "duration-300 ease-out animate-in fade-in",
            renderProps.isExiting &&
              "duration-300 ease-in animate-out fade-out",
            className,
          ),
      )}
    >
      <RACModal
        {...props}
        className={composeRenderProps(
          classNames?.content,
          (className, renderProps) =>
            twMerge(
              "h-full w-[170px] overflow-hidden text-left shadow-xl",
              "bg-bg-primary",
              renderProps.isEntering &&
                `duration-300 ease-out ${animations.enter}`,
              renderProps.isExiting &&
                `duration-300 ease-in ${animations.exit}`,
              className,
            ),
        )}
      />
    </RACModalOverlay>
  );
}

export { Dialog as SheetContent } from "react-aria-components";
